import type { ParamDefinition, X402Endpoint } from "@/hooks/useX402Endpoints";

export type X402ImportStep = "input" | "probe" | "importing" | "success";

export interface X402ImportWizardProps {
  /** Initial URL to pre-populate */
  initialUrl?: string;
  /** Called when import succeeds */
  onImported?: (endpoint: X402Endpoint) => void;
  /** Called when user cancels/closes */
  onCancel?: () => void;
  /** Called when user wants to import another (success step) */
  onImportAnother?: () => void;
  /** Whether wizard is active (for modal usage to reset state) */
  isActive?: boolean;
  /** Variant affects sizing and some UI elements */
  variant?: "modal" | "page";
}

// Network name (from x402 protocol) to chain ID mapping
export const NETWORK_TO_CHAIN_ID: Record<string, number> = {
  base: 8453,
  "base-sepolia": 84532,
  ethereum: 1,
  mainnet: 1,
  optimism: 10,
  arbitrum: 42161,
  polygon: 137,
  avalanche: 43114,
  bnb: 56,
  "bnb-chain": 56,
};

// Convert x402 network name to chain ID for token lookup
export function networkToChainId(network?: string): number | undefined {
  if (!network) return undefined;
  return NETWORK_TO_CHAIN_ID[network.toLowerCase()];
}

// Helper to build example input from discovered body params
export function buildExampleInputFromParams(bodyParams?: Record<string, ParamDefinition>): string {
  if (!bodyParams || Object.keys(bodyParams).length === 0) return "";

  const example: Record<string, unknown> = {};
  for (const [name, param] of Object.entries(bodyParams)) {
    // Use default value if available
    if (param.default !== undefined) {
      example[name] = param.default;
      continue;
    }
    // Use first enum value if available
    if (param.enum && param.enum.length > 0) {
      example[name] = param.enum[0];
      continue;
    }
    // Generate placeholder based on type
    switch (param.type) {
      case "string":
        example[name] = param.required ? `<${name}>` : "";
        break;
      case "number":
      case "integer":
        example[name] = 0;
        break;
      case "boolean":
        example[name] = false;
        break;
      case "array":
        example[name] = [];
        break;
      case "object":
        example[name] = {};
        break;
      default:
        if (param.required) {
          example[name] = `<${name}>`;
        }
    }
  }

  // Only return if we have at least one required field or default
  const hasContent = Object.keys(example).length > 0;
  return hasContent ? JSON.stringify(example, null, 2) : "";
}
